import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { GridApi } from 'ag-grid-community';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import useDebounce from 'hooks/useDebounce/useDebounce';
import {
  TExternalFilter,
  TTableFilterName,
  TColumnConfiguration,
  TMonthsFilterDateRange,
} from 'types';
import {
  APPLICATION_TABLE_KEYS,
  APPLICATION_TABLE_KEY_NAMES,
} from 'utils/applications/applications.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  DEFAULT_COLUMN_CONFIG,
  getExternalStatusFilterValue,
  getColumnReorderPayload,
} from './ApplicationTableStateContext.helpers';

type TFilterDataValue = string[] | { [key: string]: any };
type TFilterData = { filter: TTableFilterName; values: TFilterDataValue };
export type TTableStatus = 'loading' | 'empty' | 'no results' | 'success';

type TFiltersState = {
  filters: TFilterData[];
};

type TSettingsState = {
  columnConfig: TColumnConfiguration[];
  dateRange: TMonthsFilterDateRange['id'];
  includePartnerChannel: boolean;
};

export type TApplicationTableStateContextValue = TFiltersState &
  TSettingsState & {
    // Data
    numberOfRows: number | null;
    status: TTableStatus;
    initialApplicationsLoaded: boolean;
    setNumberOfRows: (numberOfRows: number | null) => void;
    setStatus: (status: TTableStatus) => void;
    setInitialApplicationsLoaded: (initialApplicationsLoaded: boolean) => void;
    gridApi: GridApi<any> | undefined;
    setGridApi: (gridApi: GridApi<any> | undefined) => void;

    // Filters
    mounted: boolean;
    saveFilter: (filter: TTableFilterName, data: TFilterDataValue) => void;
    removeFilter: (filter: TTableFilterName) => void;
    hasFilter: (filter: TTableFilterName) => boolean;
    getFilterValues: (filter: TTableFilterName) => TFilterDataValue | null;

    // Special filters
    externalStatusFilter: TExternalFilter | null;

    // Dashboard settings
    reorderColumns: (keys: TTableFilterName[]) => void;
    toggleColumnDisplay: (key: TTableFilterName) => void;
    displayAllColumns: () => void;
    setDateRange: (dateRange: TMonthsFilterDateRange['id']) => void;
    setIncludePartnerChannel: (prevValue: boolean) => void;
  };

export const ApplicationTableStateContext =
  createContext<TApplicationTableStateContextValue>({
    // Data
    numberOfRows: null,
    status: 'loading',
    initialApplicationsLoaded: false,
    setNumberOfRows: () => {},
    setStatus: () => {},
    setInitialApplicationsLoaded: () => {},
    gridApi: undefined,
    setGridApi: () => {},

    // Filters
    filters: [],
    mounted: false,
    saveFilter: () => {},
    removeFilter: () => {},
    hasFilter: () => false,
    getFilterValues: () => [] as TFilterDataValue,

    // Special filters
    externalStatusFilter: 'all',

    // Dashboard settings
    columnConfig: DEFAULT_COLUMN_CONFIG(),
    reorderColumns: () => {},
    toggleColumnDisplay: () => {},
    displayAllColumns: () => {},
    dateRange: '1 month',
    includePartnerChannel: false,
    setDateRange: () => {},
    setIncludePartnerChannel: () => {},
  });

interface ApplicationTableStateProviderProps {
  children: ReactNode;
  storeFiltersLocally?: boolean;
}

export const LOCAL_STORAGE_KEY_FILTERS = 'dashboardFilters';
export const LOCAL_STORAGE_KEY_SETTINGS = 'dashboardSettings';

export const ApplicationTableStateProvider: React.FC<
  ApplicationTableStateProviderProps
> = ({ children, storeFiltersLocally = false }) => {
  const [gridApi, setGridApi] = useState<GridApi<any> | undefined>(undefined);
  const [numberOfRows, setNumberOfRows] = useState<number | null>(null);
  const [status, setStatus] = useState<TTableStatus>('loading');
  const [filters, setFilters] = useState<TFilterData[]>([]);
  const [mounted, setMounted] = useState(storeFiltersLocally ? false : true);
  const [columnConfig, setColumnConfig] = useState<TColumnConfiguration[]>(
    DEFAULT_COLUMN_CONFIG(),
  );

  const [initialApplicationsLoaded, setInitialApplicationsLoaded] =
    useState<boolean>(false);

  const [externalStatusFilter, setExternalStatusFilter] =
    useState<TExternalFilter | null>('all');

  const [dateRange, setDateRange] =
    useState<TMonthsFilterDateRange['id']>('1 month');

  const [includePartnerChannel, setIncludePartnerChannel] = useState(false);

  const { introducer, introducerCompany } = useIntroducerContext();
  const { trackEvent } = useAmplitude();
  const introducerDataFetched = useMemo(
    () => Boolean(introducer),
    [introducer],
  );

  const defaultColumnConfig = useMemo(() => {
    const showReferredByColumn =
      introducer?.master_broker || introducerCompany?.shared_applications;

    return DEFAULT_COLUMN_CONFIG(showReferredByColumn);
  }, [introducer, introducerCompany]);

  // Track reorder (debounced)
  const { debouncedFunction: trackReorder } = useDebounce(() => {
    trackEvent(AMPLITUDE_EVENTS.columnReorder, {
      ...getColumnReorderPayload(columnConfig),
    });
  }, 2000);

  /**
   * FILTERS
   */

  // Fetch and set the tableFilters from local storage on mount (if enabled)
  useEffect(() => {
    if (storeFiltersLocally) {
      const storedState = localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS);
      if (storedState) {
        const { filters: storedFilters } = JSON.parse(storedState);
        setFilters(storedFilters);
      }
      setMounted(true);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY_FILTERS);
    }
  }, [storeFiltersLocally]);

  // Store table filters locally (if enabled)
  useEffect(() => {
    if (storeFiltersLocally) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY_FILTERS,
        JSON.stringify({
          filters,
          lastModified: new Date(),
        }),
      );
    }
  }, [filters, storeFiltersLocally]);

  const saveFilter = (filter: TTableFilterName, data: TFilterDataValue) => {
    if (filter === APPLICATION_TABLE_KEYS.status) {
      setExternalStatusFilter(getExternalStatusFilterValue(data as string[]));
    }

    setFilters((prevFilters) => {
      const existingFilterIndex = prevFilters.findIndex(
        (f) => f.filter === filter,
      );
      if (existingFilterIndex > -1) {
        const newFilters = [...prevFilters];
        newFilters[existingFilterIndex] = { filter, values: data };
        return newFilters;
      }

      return [...prevFilters, { filter, values: data }];
    });
  };

  const removeFilter = (filter: TTableFilterName) => {
    setFilters((prevFilters) => prevFilters.filter((f) => f.filter !== filter));
    if (filter === APPLICATION_TABLE_KEYS.status) {
      setExternalStatusFilter('all');
    }
  };

  const hasFilter = (filter: TTableFilterName): boolean => {
    return Boolean(filters.find((item) => item.filter === filter));
  };

  const getFilterValues = (
    filter: TTableFilterName,
  ): TFilterDataValue | null => {
    const filterObj = filters.find((f) => f.filter === filter);
    return filterObj ? filterObj.values : null;
  };

  /**
   * SETTINGS
   */

  // Fetch and set the columnConfig from local storage on mount
  useEffect(() => {
    const storedSettings = localStorage.getItem(LOCAL_STORAGE_KEY_SETTINGS);
    if (storedSettings) {
      const { dateRange, columnConfig, includePartnerChannel }: TSettingsState =
        JSON.parse(storedSettings);
      setDateRange(dateRange);

      setIncludePartnerChannel(includePartnerChannel);

      if (introducerDataFetched) {
        if (columnConfig.length === defaultColumnConfig.length) {
          setColumnConfig(
            columnConfig.map((item: any) => ({
              ...item,
              // Doing this to trigger the translations
              label: defaultColumnConfig.find((val) => val.key === item.key)
                ?.label,
            })),
          );
        } else {
          setColumnConfig(defaultColumnConfig);
        }
      }
    } else if (introducerDataFetched) {
      setColumnConfig(defaultColumnConfig);
    }
  }, [defaultColumnConfig, introducerDataFetched]);

  // Store the columnConfig in local storage whenever it changes
  useEffect(() => {
    if (introducerDataFetched) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY_SETTINGS,
        JSON.stringify({
          columnConfig,
          dateRange,
          includePartnerChannel,
          lastModified: new Date(),
        }),
      );
    }
  }, [columnConfig, dateRange, includePartnerChannel, introducerDataFetched]);

  const reorderColumns = (keys: TTableFilterName[]) => {
    setColumnConfig((prev) => {
      const newConfig: typeof columnConfig = keys.map((key, index) => {
        const originalConfig = prev.find(
          (item) => item.key === key,
        ) as TColumnConfiguration;

        return {
          key,
          position: index,
          display: originalConfig.display,
          label: originalConfig.label,
        };
      });

      return newConfig;
    });
    trackReorder();
  };

  const displayAllColumns = () => {
    trackEvent(AMPLITUDE_EVENTS.showAllColumns);
    setColumnConfig((prev) => prev.map((item) => ({ ...item, display: true })));
  };

  const toggleColumnDisplay = (key: TTableFilterName) => {
    const { display } = columnConfig.find(
      (item) => item.key === key,
    ) as TColumnConfiguration;

    trackEvent(AMPLITUDE_EVENTS.columnToggle, {
      Column: APPLICATION_TABLE_KEY_NAMES[key],
      Display: display ? 'off' : 'on',
    });

    setColumnConfig((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, display: !item.display } : item,
      ),
    );
  };

  const handleDateRangeChange = (
    newDateRange: TMonthsFilterDateRange['id'],
  ) => {
    trackEvent(AMPLITUDE_EVENTS.monthFilterSelected, {
      Range: newDateRange,
    });

    setDateRange(newDateRange);
  };

  return (
    <ApplicationTableStateContext.Provider
      value={{
        initialApplicationsLoaded,
        setInitialApplicationsLoaded,
        numberOfRows,
        setNumberOfRows,
        status,
        setStatus,
        filters,
        mounted,
        saveFilter,
        removeFilter,
        hasFilter,
        getFilterValues,
        externalStatusFilter,
        columnConfig,
        toggleColumnDisplay,
        displayAllColumns,
        reorderColumns,
        dateRange,
        setDateRange: handleDateRangeChange,
        includePartnerChannel,
        setIncludePartnerChannel,
        gridApi,
        setGridApi,
      }}
    >
      {children}
    </ApplicationTableStateContext.Provider>
  );
};

export const useApplicationTableState =
  (): TApplicationTableStateContextValue => {
    const context = useContext(ApplicationTableStateContext);
    if (!context) {
      throw new Error(
        'useApplicationTable must be used within an ApplicationTableStateProvider',
      );
    }
    return context;
  };
